import { Anchor, Stack } from "@mantine/core"
import { IconAlertCircle } from "@tabler/icons-react"
import { InfoBox, LabelWithTooltip, Select } from "@tfg/ui"
import _ from "lodash"
import { Trans, useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import { useFindAllEcoscoreDataSources } from "~/gen/api/hooks/useFindAllEcoscoreDataSources"
import { useProductSheetGroup } from "~/pages/Brand/CommunicationStudio/contexts/productSheetGroup/useProductSheetGroup"

type EcoscoreRendererProps = {
  ecoScoreDataSource: string
  updateModalConfigurations: (
    configurations: Partial<ContentFilteringProps> | null
  ) => void
  description: string
}

export const EcoscoreRenderer = ({
  ecoScoreDataSource,
  updateModalConfigurations,
  description,
}: EcoscoreRendererProps) => {
  const { t } = useTranslation("communicationStudio")
  const { selectedProductSheetGroup } = useProductSheetGroup()
  const { data: ecoScoreDataSources, isLoading } =
    useFindAllEcoscoreDataSources(selectedProductSheetGroup?.id ?? "")

  const ecoScoreDataSourcesOptions = ecoScoreDataSources?.data.sources.map(
    (source) => ({
      label: `${_.capitalize(source).replace(/_/g, " ")} (${t(`tabs.content.componentModal.ecoscore.cost`)})`,
      value: source,
    })
  )

  const handleEcoScoreDataSourceChange = (value: string | null) => {
    if (!value) {
      return
    }

    updateModalConfigurations({
      ecoScoreDataSource: value,
    })
  }

  return (
    <Stack>
      <InfoBox
        title={t("tabs.content.componentModal.ecoscore.infoTitle")}
        message={description}
      />
      <InfoBox
        variant="warning"
        icon={<IconAlertCircle />}
        title={t("tabs.content.componentModal.ecoscore.title")}
        message={
          <Trans
            ns="communicationStudio"
            i18nKey="tabs.content.componentModal.ecoscore.warning"
            components={{
              link1: (
                <Anchor
                  href="https://affichage-environnemental.ecobalyse.beta.gouv.fr"
                  c="orange.9"
                  target="_blank"
                />
              ),
              link2: (
                <Anchor
                  component={Link}
                  to="/data-transfer/export"
                  c="orange.9"
                  target="_blank"
                />
              ),
            }}
          />
        }
      />
      <Select
        label={
          <LabelWithTooltip
            label={t("tabs.content.componentModal.ecoscore.label")}
            hoverHelperText={t(
              "tabs.content.componentModal.ecoscore.labelTooltip"
            )}
            fw={600}
            withAsterisk
          />
        }
        data={ecoScoreDataSourcesOptions}
        value={ecoScoreDataSource}
        onChange={handleEcoScoreDataSourceChange}
        isLoading={isLoading}
        placeholder={t("tabs.content.componentModal.ecoscore.placeholder")}
      />
    </Stack>
  )
}
